// Top-level tmux-style layout for the Career CLI (3-pane).
//
// Composition:
//   ┌────────────────────────┬────────────────────────┐
//   │ [0] coach (careerCoach)│ [1] tech (techInspector)│
//   ├────────────────────────┴────────────────────────┤  ← horizontal splitter
//   │ [2] shell  ~/career $ _                         │  ~30% row, full width
//   ├─────────────────────────────────────────────────┤
//   │ monitor (one-line ticker)                       │  ~28px
//   ├─────────────────────────────────────────────────┤
//   │ tmux status line  [0..2]                        │  ~24px
//   └─────────────────────────────────────────────────┘
//
// Splitters:
//   - vertical splitter   -> coach width vs tech width
//   - horizontal splitter -> chat block height vs shell height
//
// (The earlier 4-pane variant — coach/tech/fun + shell — was retired in
// favour of this 3-pane layout. The Easter Egg Bot's gag commands lived in
// pane [2] fun; its only load-bearing slash was /rubik, now reachable from
// the shell via `open rubik`.)

import React, { lazy, Suspense, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import MonitorPane from './MonitorPane';
import ChatPane from './chat/ChatPane';
import type { ChatPaneAction } from './chat/ChatPane';
import { careerCoach } from './chat/agents/careerCoach';
import { techInspector } from './chat/agents/techInspector';
import { useTmuxPaneSwitcher, type PaneId } from './useTmuxPaneSwitcher';
import Splitter from './Splitter';
import type { PortfolioRepo } from './projectLoader';

// ProjectModalWithTabs ships with its own three.js/code-mirror friends, so
// lazy-load it to keep the Career CLI initial chunk lean. It only mounts
// when a visitor clicks a repo row in the git-log timeline.
const ProjectModalWithTabs = lazy(() => import('../ProjectModalWithTabs'));
import ShellPane from './ShellPane';
import RubikModal from './RubikModal';
import styles from './Terminal.module.css';

// --- Status footer -------------------------------------------------------

interface StatusFooterProps {
  active: PaneId;
}

const StatusFooter: React.FC<StatusFooterProps> = ({ active }) => {
  const item = (idx: number, label: string, id: PaneId) => {
    const isOn = active === id;
    return (
      <span className={isOn ? 'text-primary-400' : 'text-gray-500'}>
        [{idx}] {label}{isOn ? '*' : ''}
      </span>
    );
  };
  return (
    <div
      className="h-full w-full px-3 flex items-center gap-3 text-[12px]"
      style={{
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        background: '#0a0f0d',
        borderTop: '1px solid #1f2937',
      }}
    >
      {item(0, 'coach', 'coach')}
      {item(1, 'tech', 'tech')}
      {item(2, 'shell', 'shell')}
      <span className="text-gray-600">&middot;</span>
      <span className="text-gray-500">prefix=<span className="text-secondary-300">^B</span></span>
      <span className="ml-auto">
        <Link
          to="/classic"
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-primary-700 bg-primary-900/30 text-primary-300 hover:text-primary-100 hover:bg-primary-800/50 hover:border-primary-500 transition-colors font-medium"
          onMouseDown={(e) => e.stopPropagation()}
          title="Switch to the traditional resume layout"
        >
          [classic view &rarr;]
        </Link>
      </span>
    </div>
  );
};

// --- Layout root ---------------------------------------------------------

const TmuxLayout: React.FC = () => {
  const { active, setActive, prefixActive } = useTmuxPaneSwitcher('coach');

  // Rubik (now: career.log) modal visibility. Toggled by the `open-rubik`
  // action coming from either the Easter-egg bot's `/rubik` slash command
  // or `open rubik` in the shell pane.
  const [rubikOpen, setRubikOpen] = useState(false);

  // When the visitor drills into a portfolio repo from the git-log timeline,
  // the career.log modal stays mounted underneath and the project modal
  // stacks on top (zIndex 10000 > the career.log's 9999). Closing the
  // project modal reveals the timeline again — no boot re-run, no need
  // to retype /rubik.
  const [selectedRepo, setSelectedRepo] = useState<PortfolioRepo | null>(null);
  const handleOpenRepo = useCallback((repo: PortfolioRepo) => {
    setSelectedRepo(repo);
  }, []);

  /**
   * Handles slash-command side-effects from any pane.
   *
   * `open-resume-pdf` and `go-classic` route through the browser; `open-rubik`
   * flips local state to mount <RubikModal/>.
   */
  const onAction = useCallback((action: ChatPaneAction) => {
    switch (action) {
      case 'open-rubik':
        setRubikOpen(true);
        break;
      case 'open-resume-pdf':
        // Trigger the existing PDF route. The Classic page already exposes
        // PDFDownloadLink; navigating here keeps a single source of truth.
        if (typeof window !== 'undefined') {
          window.open('/classic?download=pdf', '_blank', 'noopener,noreferrer');
        }
        break;
      case 'go-classic':
        if (typeof window !== 'undefined') {
          window.location.href = '/classic';
        }
        break;
      default:
        // Exhaustiveness check — surfaces typos at compile time.
        console.warn('Unknown pane action:', action);
    }
  }, []);

  // Splitter state — percent of parent container occupied by the FIRST pane.
  //   leftWidth   : coach (left) vs tech (right) inside the chat row
  //   chatHeight  : chat block (top) vs shell row (bottom)
  // Double-click on any splitter resets to 50/70.
  const [leftWidth, setLeftWidth] = useState(50);
  const [chatHeight, setChatHeight] = useState(70);

  return (
    <div
      className="fixed inset-0 bg-surface-dark"
      style={{
        display: 'grid',
        // Outer grid: chat+shell area (wrapped) / monitor / status footer.
        gridTemplateRows: '1fr 28px 24px',
      }}
    >
      {/* Wrapper that holds the chat block and the shell row, split by a
          horizontal splitter so the shell can be resized vs the chat panes. */}
      <div
        style={{
          display: 'grid',
          gridTemplateRows: `${chatHeight}% 6px 1fr`,
          minHeight: 0,
          minWidth: 0,
        }}
      >
        {/* Chat block — vertical split between coach (left) and tech (right). */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `${leftWidth}% 6px 1fr`,
            minHeight: 0,
            minWidth: 0,
          }}
        >
          {/* Coach pane — left column. */}
          <div style={{ minHeight: 0, minWidth: 0 }}>
            <ChatPane
              config={careerCoach}
              isActive={active === 'coach'}
              onActivate={() => setActive('coach')}
              onAction={onAction}
            />
          </div>

          {/* Vertical splitter — drag to resize left/right. */}
          <Splitter direction="vertical" onResize={setLeftWidth} />

          {/* Tech pane — right column. */}
          <div style={{ minHeight: 0, minWidth: 0 }}>
            <ChatPane
              config={techInspector}
              isActive={active === 'tech'}
              onActivate={() => setActive('tech')}
              onAction={onAction}
            />
          </div>
        </div>

        {/* Horizontal splitter — divides the chat block from the shell row. */}
        <Splitter direction="horizontal" onResize={setChatHeight} />

        {/* Shell pane — full-width row at the bottom of the chat area. */}
        <div style={{ minHeight: 0, minWidth: 0 }}>
          <ShellPane
            isActive={active === 'shell'}
            onActivate={() => setActive('shell')}
            onAction={onAction}
          />
        </div>
      </div>

      {/* Monitor pane — single full-width row above status. */}
      <div className="border-t border-gray-800" style={{ minHeight: 0, minWidth: 0 }}>
        <MonitorPane isActive={false} />
      </div>

      {/* tmux status footer. */}
      <div style={{ minHeight: 0, minWidth: 0 }}>
        <StatusFooter active={active} />
      </div>

      {/* Prefix-pressed indicator — tiny pulsing green dot in the bottom-right.
          Lives outside the grid so it can sit over the footer without
          disturbing layout. */}
      {prefixActive && (
        <div
          className={styles.prefixDot}
          aria-hidden="true"
          title="tmux prefix armed"
        />
      )}

      {/* Rubik modal — conditional mount so each open re-runs the boot
          sequence (state lives inside the modal). Renders through a portal
          to document.body, so the grid layout above doesn't reserve space. */}
      {rubikOpen && (
        <RubikModal
          isOpen
          onClose={() => setRubikOpen(false)}
          onOpenProject={handleOpenRepo}
        />
      )}

      {/* Repo deep-dive modal — portaled to <body> so it leaves the
          TmuxLayout's `fixed` stacking context. Without the portal it would
          render inside TmuxLayout (a fixed-positioned ancestor, which
          itself creates a stacking context), and our zIndex=10000 would
          only apply within that context — visually it would sit BENEATH
          the career.log modal, which portals to body with z-9999.
          Lazy because it drags in code-mirror / extra bits we don't want
          in the initial Career CLI chunk. */}
      {selectedRepo &&
        typeof document !== 'undefined' &&
        createPortal(
          <Suspense fallback={null}>
            <ProjectModalWithTabs
              isOpen={true}
              onClose={() => setSelectedRepo(null)}
              projects={[selectedRepo]}
              initialProjectIndex={0}
              isDarkMode={true}
              zIndex={10000}
              captureEsc={true}
            />
          </Suspense>,
          document.body,
        )}
    </div>
  );
};

export default TmuxLayout;
